import { Grid, Space, Typography, theme } from "../ui";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { PageContainer } from "../layout/PageContainer";
import { appShellMetrics, spacing } from "../layout/spacing";
import { ONBOARDING_PAGE_MAX_WIDTH, type OnboardingBrand } from "./OnboardingPageFrame";
import { windowHeight } from "../theme/displayZoom";

/**
 * Vollflächige Seite ohne App-Shell für Tarifabläufe — Einrichtung, Verlängerung und Tarifwechsel
 * sehen gleich aus. Die Shell bleibt für den Produktkontext montiert, ist aber verdeckt und inert.
 *
 * Kopf bewusst einzeilig statt `PageHeader`: Marke links, Titel mittig, Aktion rechts — so rückt der
 * Inhalt eine Zeile nach oben. Auf schmalen Bildschirmen steht der Titel mittig unter Marke und Aktion.
 */
export function FullPageOverlay({ brand, title, extra, children }: {
  brand?: OnboardingBrand;
  title: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
}) {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const compact = !screens.md;
  const anchor = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let appRoot = anchor.current as HTMLElement | null;
    while (appRoot?.parentElement && appRoot.parentElement !== document.body) appRoot = appRoot.parentElement;
    if (!appRoot || appRoot.parentElement !== document.body) return;
    const root = appRoot;
    const previousOverflow = document.documentElement.style.overflow;
    root.setAttribute("inert", "");
    document.documentElement.style.overflow = "hidden";
    return () => { root.removeAttribute("inert"); document.documentElement.style.overflow = previousOverflow; };
  }, []);

  const brandNode = brand ? (
    <Space size={spacing.tightGap}>
      {brand.logo && <img src={brand.logo} alt="" height={appShellMetrics.logoHeight} />}
      <Typography.Text strong>{brand.name}</Typography.Text>
    </Space>
  ) : <span />;
  const titleNode = <Typography.Title level={4} style={{ margin: 0, textAlign: "center" }}>{title}</Typography.Title>;

  const page = <div style={{ position: "fixed", inset: 0, zIndex: token.zIndexPopupBase - 1, overflowY: "auto", background: token.colorBgLayout }}>
    <PageContainer maxWidth={ONBOARDING_PAGE_MAX_WIDTH} style={{ marginInline: "auto", padding: spacing.pageGap, minHeight: windowHeight("100dvh") }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: compact ? "1fr auto" : "1fr auto 1fr",
        alignItems: "center",
        gap: spacing.elementGap,
        marginBottom: spacing.sectionGap,
      }}>
        {brandNode}
        {!compact && titleNode}
        <div style={{ justifySelf: "end" }}>{extra}</div>
        {compact && <div style={{ gridColumn: "1 / -1" }}>{titleNode}</div>}
      </div>
      {children}
    </PageContainer>
  </div>;
  return <><span ref={anchor} hidden />{createPortal(page, document.body)}</>;
}
